import React, { useEffect, useState } from 'react';
import { projectAPI, staffAPI } from '../services/api';
import { Button, Modal, Table, Tag, Form, Input, InputNumber, Select, Popconfirm, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { formatCurrencySimple } from '../utils/currency';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY_CODE, DEFAULT_PHONE_CODE, PHONE_CODE_OPTIONS } from '../constants/localeOptions';
import StudioNavBar from '../components/common/StudioNavBar';
import { useAuth } from '../context/AuthContext';

const statusColors: Record<string, string> = {
  new: 'blue',
  recording: 'purple',
  mixing: 'orange',
  mastering: 'gold',
  delivered: 'green',
};

const Projects: React.FC = () => {
  const { user, isStaff } = useAuth();
  const [projects, setProjects] = useState<any[]>([]);
  const [producers, setProducers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [viewingProject, setViewingProject] = useState<any | null>(null);
  const [editingProject, setEditingProject] = useState<any | null>(null);
  const [attentionList, setAttentionList] = useState<any[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [form] = Form.useForm();
  const selectedCurrency = Form.useWatch('currency_code', form) || DEFAULT_CURRENCY_CODE;

  const canEditProject = (project: any) => {
    if (!isStaff) return true;
    return project.assigned_producer === Number(user?.id);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleView = (project: any) => {
    setViewingProject(project);
  };

  const handleEdit = (project: any) => {
    if (isStaff && !canEditProject(project)) {
      message.error('You can only edit projects assigned to you');
      return;
    }

    setEditingProject(project);
    const formValues = {
      custom_id: project.custom_id,
      song_title: project.song_title,
      client_name: project.client_name,
      client_phone_country_code: project.client_phone_country_code || DEFAULT_PHONE_CODE,
      client_phone: project.client_phone,
      currency_code: project.currency_code || DEFAULT_CURRENCY_CODE,
      status: project.status,
      assigned_producer: project.assigned_producer,
      agreed_price: project.agreed_price,
      notes: project.notes,
    };

    setTimeout(() => {
      form.setFieldsValue(formValues);
    }, 0);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await projectAPI.deleteProject(id);
      message.success('Project deleted successfully');
      fetchProjects();
    } catch (err) {
      message.error('Failed to delete project');
    }
  };

  const responsiveSm: Breakpoint[] = ['sm'];
  const responsiveMd: Breakpoint[] = ['md'];
  const responsiveLg: Breakpoint[] = ['lg'];

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'custom_id',
      key: 'custom_id',
      sorter: true,
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => record.custom_id || record.id
    },
    {
      title: 'Song',
      dataIndex: 'song_title',
      key: 'song_title',
      sorter: true,
      width: 150,
      ellipsis: true
    },
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      sorter: true,
      width: 130,
      ellipsis: true,
      responsive: responsiveSm
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => <Tag color={statusColors[status] || 'default'}>{status}</Tag>,
      filters: [
        { text: 'New', value: 'new' },
        { text: 'Recording', value: 'recording' },
        { text: 'Mixing', value: 'mixing' },
        { text: 'Mastering', value: 'mastering' },
        { text: 'Delivered', value: 'delivered' },
      ]
    },
    {
      title: 'Producer',
      dataIndex: 'assigned_producer_name',
      key: 'assigned_producer_name',
      sorter: true,
      width: 140,
      ellipsis: true,
      responsive: responsiveMd
    },
    {
      title: 'Price',
      dataIndex: 'agreed_price',
      key: 'agreed_price',
      width: 140,
      responsive: responsiveLg,
      render: (value: any, record: any) => formatCurrencySimple(value, record.currency_code)
    },
    {
      title: 'Paid',
      dataIndex: 'total_paid',
      key: 'total_paid',
      width: 140,
      responsive: responsiveLg,
      render: (value: any, record: any) => formatCurrencySimple(value, record.currency_code)
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: 140,
      render: (value: any, record: any) => {
        const num = Number(value);
        if (isNaN(num)) return <span>{formatCurrencySimple(0, record.currency_code)}</span>;
        return (
          <span style={{ color: num > 0 ? 'red' : 'green', fontWeight: 'bold' }}>
            {formatCurrencySimple(value, record.currency_code)}
          </span>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: any) => {
        const canEdit = canEditProject(record);
        return (
          <div className="flex gap-1 flex-wrap">
            {!canEdit ? (
              <Button icon={<EyeOutlined />} onClick={() => handleView(record)} size="small">
                View
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  className="hidden sm:inline-flex"
                >
                  Edit
                </Button>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  className="sm:hidden"
                />
                <Popconfirm
                  title="Delete project?"
                  description="Are you sure you want to delete this project?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
              </>
            )}
          </div>
        );
      }
    },
  ];

  const attentionListColumns = [
    { title: 'Project ID', dataIndex: 'custom_id', key: 'custom_id', sorter: true, render: (_: any, record: any) => record.custom_id || record.id },
    { title: 'Song Title', dataIndex: 'song_title', key: 'song_title', sorter: true },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (status: string) => <Tag color={statusColors[status] || 'default'}>{status}</Tag> },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (value: any, record: any) => {
        const num = Number(value);
        if (isNaN(num)) return <span>{formatCurrencySimple(0, record.currency_code)}</span>;
        return <span style={{ color: num > 0 ? 'red' : 'green' }}>{formatCurrencySimple(value, record.currency_code)}</span>;
      }
    },
  ];

  useEffect(() => {
    fetchProjects();
    fetchProducers();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await projectAPI.getProjects();
      const projectData = res.data || [];
      setProjects(projectData);
      setAttentionList(projectData.filter((project: any) => Number(project.balance) > 0));
    } catch (err) {
      message.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducers = async () => {
    try {
      const res = await staffAPI.getStaff();
      setProducers(res.data || []);
    } catch (err) {
      message.error('Failed to fetch producers');
    }
  };

  const handleAdd = () => {
    setEditingProject(null);
    form.resetFields();
    form.setFieldsValue({
      assigned_producer: isStaff && user?.id ? Number(user.id) : undefined,
      client_phone_country_code: DEFAULT_PHONE_CODE,
      currency_code: DEFAULT_CURRENCY_CODE,
      status: 'new',
    });
    setShowModal(true);
  };

  const handleSave = async (values: any) => {
    try {
      if (values.custom_id) {
        const duplicateExists = projects.some((project: any) =>
          project.custom_id === values.custom_id &&
          (!editingProject || project.id !== editingProject.id)
        );

        if (duplicateExists) {
          message.error(`Project with ID "${values.custom_id}" already exists in your studio`);
          return;
        }
      }

      const formattedValues = {
        custom_id: values.custom_id || null,
        song_title: values.song_title,
        client_name: values.client_name,
        client_phone_country_code: values.client_phone_country_code || DEFAULT_PHONE_CODE,
        client_phone: values.client_phone,
        currency_code: values.currency_code || DEFAULT_CURRENCY_CODE,
        status: values.status,
        assigned_producer: isStaff ? Number(user?.id) : values.assigned_producer,
        agreed_price: values.agreed_price,
        notes: values.notes || '',
      };

      if (editingProject) {
        await projectAPI.updateProject(editingProject.id, formattedValues);
      } else {
        await projectAPI.createProject(formattedValues);
      }
      setShowModal(false);
      form.resetFields();
      message.success('Project saved successfully');
      fetchProjects();
    } catch (err: any) {
      console.error('Save error:', err);
      if (err.response?.data?.custom_id) {
        message.error(err.response.data.custom_id[0]);
      } else {
        message.error('Failed to save project');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <StudioNavBar />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Projects</h1>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">Track every song, client, and studio assignment in one view.</p>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} size="large" className="w-full sm:w-auto">
            Add Project
          </Button>
        </div>

        <div className="mb-8 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={projects}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 10, responsive: true }}
              onRow={(record: any) => ({ onDoubleClick: () => handleEdit(record) })}
              rowClassName={(record: any) => Number(record.balance) > 0 ? 'bg-red-50' : ''}
              scroll={{ x: 'max-content' }}
              size="middle"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
            <h2 className="text-lg font-semibold text-slate-900 sm:text-xl">Attention List</h2>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">Projects with outstanding balance</p>
          </div>
          <div className="overflow-x-auto">
            <Table
              columns={attentionListColumns}
              dataSource={attentionList}
              rowKey="id"
              pagination={false}
              size="small"
              rowClassName="bg-yellow-50"
            />
          </div>
        </div>
      </main>

      <Modal
        title={editingProject ? 'Edit Project' : 'Add Project'}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingProject ? 'Update' : 'Create'}
        width={isMobile ? '95vw' : 600}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} preserve={false}>
          <Form.Item name="custom_id" label="Project ID">
            <Input placeholder="Enter project ID" size="large" />
          </Form.Item>

          <Form.Item name="song_title" label="Song Title" rules={[{ required: true, message: 'Please enter song title' }]}>
            <Input placeholder="Enter song title" size="large" />
          </Form.Item>

          <Form.Item name="client_name" label="Client Name" rules={[{ required: true, message: 'Please enter client name' }]}>
            <Input placeholder="Enter client name" size="large" />
          </Form.Item>

          <Form.Item name="client_phone" label="Client Phone" rules={[{ required: true, message: 'Please enter client phone' }]}>
            <Input
              placeholder="Enter client phone"
              size="large"
              addonBefore={
                <Form.Item name="client_phone_country_code" noStyle initialValue={DEFAULT_PHONE_CODE}>
                  <Select
                    options={PHONE_CODE_OPTIONS}
                    style={{ width: isMobile ? 110 : 130 }}
                    popupMatchSelectWidth={false}
                  />
                </Form.Item>
              }
            />
          </Form.Item>

          <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select status' }]}>
            <Select
              placeholder="Select status"
              size="large"
              options={[
                { value: 'new', label: 'New' },
                { value: 'recording', label: 'Recording' },
                { value: 'mixing', label: 'Mixing' },
                { value: 'mastering', label: 'Mastering' },
                { value: 'delivered', label: 'Delivered' }
              ]}
            />
          </Form.Item>

          <Form.Item name="assigned_producer" label="Assigned Producer" rules={[{ required: true, message: 'Please select a producer' }]}>
            <Select
              placeholder="Select a producer"
              size="large"
              showSearch
              disabled={isStaff}
              optionFilterProp="children"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={producers.map(producer => ({
                value: producer.id,
                label: `${producer.first_name} ${producer.last_name}`
              }))}
            />
          </Form.Item>
          {isStaff && (
            <p className="-mt-4 mb-4 text-xs text-gray-500">
              As a producer, projects are automatically assigned to you.
            </p>
          )}

          <Form.Item name="agreed_price" label="Agreed Price" rules={[{ required: true, message: 'Please enter price' }]}>
            <InputNumber
              style={{ width: '100%' }}
              addonBefore={
                <Form.Item name="currency_code" noStyle initialValue={DEFAULT_CURRENCY_CODE}>
                  <Select
                    options={CURRENCY_OPTIONS.map(option => ({ label: option.shortLabel, value: option.value }))}
                    style={{ width: 95 }}
                    popupMatchSelectWidth={false}
                  />
                </Form.Item>
              }
              size="large"
              min={0}
              precision={selectedCurrency === 'USD' ? 2 : 0}
              placeholder="Enter agreed price"
            />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Enter any notes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Project Details"
        open={!!viewingProject}
        onCancel={() => setViewingProject(null)}
        footer={[
          <Button key="close" onClick={() => setViewingProject(null)}>
            Close
          </Button>
        ]}
        width={isMobile ? '95vw' : 500}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        {viewingProject && (
          <div className="space-y-3">
            <div><strong>Project ID:</strong> {viewingProject.custom_id || viewingProject.id}</div>
            <div><strong>Song Title:</strong> {viewingProject.song_title}</div>
            <div><strong>Client:</strong> {viewingProject.client_name}</div>
            <div><strong>Phone:</strong> {(viewingProject.client_phone_country_code || DEFAULT_PHONE_CODE)} {viewingProject.client_phone}</div>
            <div><strong>Status:</strong> <Tag color={statusColors[viewingProject.status] || 'default'}>{viewingProject.status}</Tag></div>
            <div><strong>Assigned Producer:</strong> {viewingProject.assigned_producer_name || 'N/A'}</div>
            <div><strong>Currency:</strong> {viewingProject.currency_code || DEFAULT_CURRENCY_CODE}</div>
            <div><strong>Agreed Price:</strong> {formatCurrencySimple(viewingProject.agreed_price, viewingProject.currency_code)}</div>
            <div><strong>Total Paid:</strong> {formatCurrencySimple(viewingProject.total_paid, viewingProject.currency_code)}</div>
            <div><strong>Balance:</strong> <span style={{ color: Number(viewingProject.balance) > 0 ? 'red' : 'green' }}>{formatCurrencySimple(viewingProject.balance, viewingProject.currency_code)}</span></div>
            <div>
              <strong>Scheduled Sessions:</strong>
              {viewingProject.scheduled_sessions?.length ? (
                <div className="mt-2 space-y-2">
                  {viewingProject.scheduled_sessions.map((session: any) => (
                    <div key={session.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                      <div><strong>Room:</strong> {session.studio_room}</div>
                      <div><strong>Producer:</strong> {session.producer_name || viewingProject.assigned_producer_name || 'N/A'}</div>
                      <div><strong>Start:</strong> {new Date(session.start_time).toLocaleString()}</div>
                      <div><strong>End:</strong> {new Date(session.end_time).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <span> None</span>
              )}
            </div>
            {viewingProject.notes && <div><strong>Notes:</strong> {viewingProject.notes}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Projects;

